/**
 * Tema claro u oscuro.
 *
 * La tienda arranca SIEMPRE en claro, aunque el sistema esté en oscuro: es la
 * decisión del prototipo aprobado, y para el público de la tienda el claro
 * es el que se lee sin pensarlo. El botón de la cabecera deja cambiarlo y la
 * elección queda guardada en el navegador.
 *
 * Quien aplica el tema en el primer pintado es el script de `index.html`, para
 * que la página no aparezca clara y salte a oscura. Este hook toma ese valor ya
 * puesto y se encarga de los cambios de ahí en adelante.
 */
import { useCallback, useState } from 'react';

export type Tema = 'claro' | 'oscuro';

/** Misma clave que usa el script de arranque de `index.html`. */
export const CLAVE_TEMA = 'bandu:tema';

/** Valor del atributo `data-theme`, que es lo que leen los tokens de CSS. */
const ATRIBUTO: Record<Tema, string> = { claro: 'light', oscuro: 'dark' };

function temaActual(): Tema {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'oscuro' : 'claro';
}

export function useTema(): { tema: Tema; alternar: () => void } {
  const [tema, setTema] = useState<Tema>(temaActual);

  const alternar = useCallback(() => {
    setTema((actual) => {
      const siguiente: Tema = actual === 'claro' ? 'oscuro' : 'claro';
      document.documentElement.setAttribute('data-theme', ATRIBUTO[siguiente]);
      try {
        localStorage.setItem(CLAVE_TEMA, siguiente);
      } catch {
        // Navegación privada o almacenamiento bloqueado: el tema vale solo
        // para esta visita, que es preferible a caerse.
      }
      return siguiente;
    });
  }, []);

  return { tema, alternar };
}
